/**
 * Auth Callback Page
 *
 * Handles OAuth and magic link callbacks client-side.
 *
 * Supabase sends users here with either:
 *   - A hash fragment: #access_token=...&type=... (implicit flow, legacy)
 *   - A query param: ?code=... (PKCE flow, default for email OTP/magic link)
 *   - Error params: ?error=...&error_code=...&error_description=... (failed verify)
 *
 * With `flowType: 'pkce'` and `detectSessionInUrl: true`, the Supabase client
 * automatically detects `?code=` and calls `exchangeCodeForSession` during
 * initialization. We listen for `onAuthStateChange` to know when the session
 * is established, then redirect to the dashboard.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Set a timeout so we don't wait forever
    const timeout = setTimeout(() => {
      setError("Authentication timed out. Please try signing in again.");
    }, 15000);

    // Check for error params first — Supabase sends errors as both
    // query params and hash fragments when verify fails
    const url = new URL(window.location.href);
    const hashParams = new URLSearchParams(url.hash.substring(1));

    const errorParam = url.searchParams.get("error") || hashParams.get("error");
    const errorDescription =
      url.searchParams.get("error_description") ||
      hashParams.get("error_description");
    const errorCode =
      url.searchParams.get("error_code") || hashParams.get("error_code");

    if (errorParam) {
      clearTimeout(timeout);
      const message =
        errorCode === "otp_expired"
          ? "This link has expired. Please request a new one."
          : errorDescription?.replace(/\+/g, " ") ||
            "Authentication failed. Please try again.";
      setError(message);
      return;
    }

    // Listen for auth state changes — the Supabase client with
    // flowType:'pkce' + detectSessionInUrl:true will automatically
    // detect ?code= in the URL and exchange it for a session.
    // We also handle hash-based flows (OAuth implicit) the same way.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (
        event === "SIGNED_IN" ||
        event === "TOKEN_REFRESHED" ||
        event === "INITIAL_SESSION"
      ) {
        if (session) {
          clearTimeout(timeout);
          subscription.unsubscribe();
          navigate("/dashboard", { replace: true });
        }
      }
    });

    // Fallback: check if session is already available
    // (e.g., detectSessionInUrl already ran before this effect)
    supabase.auth.getSession().then(({ data: { session }, error: err }) => {
      if (err) {
        clearTimeout(timeout);
        subscription.unsubscribe();
        setError(err.message);
        return;
      }
      if (session) {
        clearTimeout(timeout);
        subscription.unsubscribe();
        navigate("/dashboard", { replace: true });
      }
    });

    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-primary)]">
        <div className="text-center max-w-md mx-auto p-8">
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)] mb-4">
            Authentication Error
          </h1>
          <p className="text-[var(--color-text-secondary)] mb-6">{error}</p>
          <button
            onClick={() => navigate("/login", { replace: true })}
            className="px-4 py-2 bg-[var(--color-primary-500)] text-white rounded-md hover:bg-[var(--color-primary-600)] transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-primary)]">
      <div className="text-center space-y-4">
        <div
          className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[var(--color-primary-500)] border-r-transparent"
          role="status"
        />
        <p className="text-sm text-[var(--color-text-secondary)]">
          Completing sign in...
        </p>
      </div>
    </div>
  );
}
